import {CopilotChatIntents, type GeneratedSuggestion} from '@github-ui/copilot-chat-types'

function withTitles(suggestionsByType: Record<string, GeneratedSuggestion[]>): Record<string, GeneratedSuggestion[]> {
  return Object.fromEntries(
    Object.entries(suggestionsByType).map(([key, suggestions]) => [
      key,
      suggestions.map(suggestion => ({
        ...suggestion,
        title: suggestion.title ?? suggestion.question,
      })),
    ]),
  )
}

export function getFailedActionsJobPrompt(jobId?: number, shouldFetchLogs = false) {
  const prompt = jobId != null ? `Explain why job ${jobId} failed.` : 'Explain why this job failed.'
  return shouldFetchLogs ? `${prompt} Use the provided reference to fetch the job logs.` : prompt
}

export function getExplainSnippetPrompt() {
  return 'Explain this snippet'
}

export function getSuggestImprovementsPrompt() {
  return 'Suggest improvements to this code.'
}

export function getExplainDiffPrompt() {
  return 'Explain this diff'
}

export const suggestedPrompts: Record<string, string[]> = {
  'Languages & frameworks': [
    'Show me Python beginner projects.',
    "Explain Java's garbage collection.",
    'Start me off with Node.js.',
    'Introduce me to Django best practices.',
  ],
  'Tools & environments': [
    'Set up a local development environment.',
    'Demonstrate the basics of Docker.',
    'Get me started with Git.',
    'Recommend popular VS Code extensions.',
  ],
  'Open source & contribution': [
    'Suggest 10 open source projects I can contribute to.',
    'Walk me through the GitHub Pull Request flow.',
    'How do I start my own open source project?',
    "Guide me through contributing to React's codebase.",
  ],
  'Best practices & concepts': [
    'Explain the SOLID principles of object-oriented design.',
    'Introduce me to test-driven development.',
    'Describe common design patterns.',
    'Teach me about RESTful API design.',
  ],
  'Web development': [
    'Guide me through creating a basic website.',
    'Introduce HTML5 and CSS3 features.',
    'Explain responsive web design.',
    'Start me off with Tailwind CSS.',
  ],
  'Databases & data': [
    'Get me started with SQL queries.',
    'Recommend popular NoSQL databases.',
    'How to back up a database?',
    'Give a walkthrough on normalizing a database.',
  ],
  'Algorithms & data structures': [
    'Teach me basic sorting algorithms.',
    'Explain binary search trees.',
    'Introduce me to graph algorithms.',
    'What is a hash table?',
  ],
  'Security & authentication': [
    'Give a guide on basic web security.',
    'Show me how to set up OAuth.',
    "What's a JSON Web Token?",
    'Describe common encryption techniques.',
  ],
  'Mobile development': [
    'Kickstart my journey with Android development.',
    'Introduce me to iOS app basics.',
    'Recommend cross-platform mobile frameworks.',
    'Give a guide to the app store submission process.',
  ],
  'Cloud & DevOps': [
    'Start me off with AWS basics.',
    'How do I deploy apps on Azure DevOps?',
    'Introduce me to Kubernetes.',
    'What are the basics of continuous integration/continuous deployment?',
  ],
  'Frontend frameworks & libraries': [
    'Get me started with React.',
    'Walk me through Vue.js essentials.',
    'What are some best practices in Angular development?',
    'How do I use Svelte for web apps?',
  ],
  'Performance & optimization': [
    'Teach me about website performance optimization.',
    'Explain database indexing benefits.',
    'What are some tips to optimize JavaScript code?',
    'Give a guide to efficient API caching.',
  ],
}

const threadSuggestions: Record<string, GeneratedSuggestion[]> = withTitles({
  repository: [
    {title: 'What can I ask', question: 'What questions can I ask?', iconKey: 'ask'},
    {title: 'What does this do', question: 'What does this repository do?', iconKey: 'overview'},
    {title: 'Get started', question: 'How should I get started exploring this repo?', iconKey: 'getting-started'},
    {title: 'About this repo', question: 'Can you tell me about this repository?', iconKey: 'summarize'},
  ],
  issue: [
    {title: 'Summarize', question: 'Summarize this issue.', iconKey: 'summarize'},
    {title: 'Main points', question: 'What are the main points being discussed here?', iconKey: 'overview'},
    {title: 'Next steps', question: 'Suggest next steps for this issue.', iconKey: 'next-steps'},
  ],
  alert: [{title: 'Summarize', question: 'Summarize this alert.', iconKey: 'summarize'}],
  file: [
    {title: 'Explain', question: 'Explain this file.', iconKey: 'explain'},
    {title: 'Summarize', question: 'Summarize this file for me.', iconKey: 'summarize'},
    {title: 'Improve', question: 'How can I improve this file?', iconKey: 'improve'},
  ],
  snippet: [
    {title: 'Explain', question: 'Explain this code.', iconKey: 'explain'},
    {title: 'Improve', question: 'How can I improve this code?', iconKey: 'improve'},
    {title: 'What it does', question: 'What does this code do?', iconKey: 'overview'},
  ],
  'thread-scoped-snippet': [
    {title: 'Explain', question: 'Explain this code.', iconKey: 'explain'},
    {title: 'Improve', question: 'How can I improve this code?', iconKey: 'improve'},
    {title: 'What it does', question: 'What does this code do?', iconKey: 'overview'},
  ],
  'diff-hunk': [
    {title: 'Explain changes', question: 'Explain these changes.', iconKey: 'changes'},
    {title: 'What it does', question: 'What does this diff do?', iconKey: 'explain'},
    {title: 'Improve', question: 'How can I improve this code?', iconKey: 'improve'},
  ],
  'file-diff': [
    {title: 'Explain changes', question: 'Explain these changes.', iconKey: 'changes'},
    {title: 'What it does', question: 'What does this diff do?', iconKey: 'explain'},
    {title: 'Summarize', question: 'Summarize the changes in this file.', iconKey: 'summarize'},
  ],
  'pull-request': [
    {title: 'Summarize', question: 'Summarize this pull request.', iconKey: 'summarize'},
    {title: 'Commits', question: 'What commits are included in this PR?', iconKey: 'commits'},
    {title: 'Changes', question: 'Can you tell me about the changes in this PR?', iconKey: 'changes'},
  ],
  discussion: [
    {title: 'Summarize', question: 'Summarize this discussion.', iconKey: 'summarize'},
    {title: 'User comments', question: 'Summarize the comments made by user in a discussion.', iconKey: 'overview'},
    {title: 'Key decisions', question: 'What were some key decisions made in this discussion?', iconKey: 'next-steps'},
  ],
  job: [
    {
      question: 'Why did this job fail?',
      intent: CopilotChatIntents.actionsAgent,
      iconKey: 'logs',
    },
    {
      question: 'How can I fix this build?',
      intent: CopilotChatIntents.actionsAgent,
      iconKey: 'improve',
    },
  ],
  'secret-protection-assessment': [
    {question: 'Why should I enable secret protection?', iconKey: 'security'},
    {question: 'What kind of credentials does secret scanning find?', iconKey: 'security'},
    {question: 'What is the impact of leaked secret in one of my repositories?', iconKey: 'security'},
  ],
  'code-security-assessment': [
    {question: 'Why should I enable code security?', iconKey: 'security'},
    {question: 'What kind of vulnerabilities does code security scanning find?', iconKey: 'security'},
    {question: 'What is the impact of a vulnerability in my repo?', iconKey: 'security'},
  ],
  'security-risk-assessment': [
    {
      question: 'What is the difference between a Code Security Risk Assessment and a Secret Risk Assessment?',
      iconKey: 'security',
    },
    {question: 'How is my data used during a Risk Assessment?', iconKey: 'security'},
    {question: 'Who will be notified of findings from a Risk Assessment?', iconKey: 'security'},
  ],
  default: [
    {question: 'What questions can I ask?', iconKey: 'ask'},
    {question: 'What is the best way to get started with Copilot?', iconKey: 'getting-started'},
    {question: 'How do I clone this repository?', iconKey: 'getting-started'},
    {question: 'How do I revert a commit?', iconKey: 'changes'},
    {question: 'How do I add myself as a reviewer?', iconKey: 'review'},
    {question: 'How do I create a repository?', iconKey: 'getting-started'},
  ],
  issues: [
    {question: 'How do I create an issue?', iconKey: 'getting-started'},
    {question: 'How do I filter issues by label?', iconKey: 'filter'},
    {question: 'What are the most recently updated issues?', iconKey: 'search'},
  ],
  'pull-requests': [
    {question: 'How do I create a pull request?', iconKey: 'getting-started'},
    {question: 'How do I filter pull requests by label?', iconKey: 'filter'},
    {question: 'How do I reopen a closed pull request?', iconKey: 'next-steps'},
  ],
  discussions: [
    {question: 'How do I start a new discussion?', iconKey: 'getting-started'},
    {question: 'How do I filter discussions by category or tag?', iconKey: 'filter'},
    {question: 'How do I search for a specific discussion?', iconKey: 'search'},
  ],
})

export const updatedThreadSuggestions: Record<string, GeneratedSuggestion[]> = withTitles({
  ...threadSuggestions,
  repository: [
    {title: 'Repo overview', question: 'Give me a high level overview of this repo', iconKey: 'overview'},
    {
      title: 'Performance issues',
      question: 'What are potential performance issues in this code?',
      iconKey: 'performance',
    },
    {title: 'Recent PRs', question: 'Summarize the last 3 merged PRs in this repo', iconKey: 'summarize'},
  ],
  'pull-request': [
    {title: 'Summarize changes', question: 'Summarize the high level changes made', iconKey: 'summarize'},
    {title: 'Help me review', question: 'Help me review this pull request', iconKey: 'review'},
  ],
  'file-diff': [
    {title: 'Explain changes', question: 'Explain these changes.', iconKey: 'changes'},
    {title: 'Identify risks', question: 'Identify potential risks from this change.', iconKey: 'improve'},
    {title: 'Help me review', question: 'Help me review this pull request.', iconKey: 'review'},
  ],
  issue: [
    {title: 'Summarize', question: 'Summarize this issue.', iconKey: 'summarize'},
    {title: 'Next steps', question: 'Suggest next steps for this issue.', iconKey: 'next-steps'},
    {title: 'Create PR', question: 'Create a PR to solve this issue.', iconKey: 'create-pr'},
  ],
  discussion: [
    {
      title: 'Summarize',
      question: 'Summarize this discussion. What were some key decisions made in this discussion?',
      iconKey: 'summarize',
    },
    {title: 'Comments', question: 'Summarize the comments made on this discussion.', iconKey: 'overview'},
    {title: 'Next steps', question: 'Suggest next steps based on this discussion', iconKey: 'next-steps'},
  ],
  snippet: [
    {title: 'Explain', question: 'Explain this code.', iconKey: 'explain'},
    {title: 'Improve', question: 'How can I improve this code?', iconKey: 'improve'},
    {title: 'Walk me through', question: 'What does this code do?', iconKey: 'overview'},
  ],
  file: [
    {title: 'Summarize', question: 'Summarize this file for me.', iconKey: 'summarize'},
    {title: 'Improve', question: 'How can I improve this file?', iconKey: 'improve'},
    {
      title: 'File structure',
      question: 'List and define the functions and variables in this file',
      iconKey: 'overview',
    },
  ],
  default: [
    {title: 'Ask questions', question: 'What questions can I ask?', iconKey: 'ask'},
    {title: 'Get started', question: 'What is the best way to get started with Copilot?', iconKey: 'getting-started'},
    {title: "What's new", question: "What's new in Copilot?", iconKey: 'overview'},
  ],
})

export const unresolvedFeedbackSuggestion: GeneratedSuggestion = {
  title: 'Unresolved feedback',
  question: 'What feedback is unresolved?',
  prompt:
    "For each review comment on this pull request, check whether the current pull request code makes the change the reviewer asked for. Use the code as the source of truth, not the comment's resolved status. List the feedback the code hasn't addressed yet.",
  iconKey: 'feedback',
}

export function getThreadSuggestions(): Record<string, GeneratedSuggestion[]> {
  const base = updatedThreadSuggestions

  return {
    ...base,
    'pull-request': [...(base['pull-request'] ?? []), unresolvedFeedbackSuggestion],
  }
}
